import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import styled from 'styled-components';
import LinkedText from '../LinkedText';
import { DiscussionReactionBar } from './DiscussionReactionBar';
import {
  teacherService,
  type DiscussionReactionSummary,
  type TeacherClassDiscussion,
  type TeacherDiscussionPost,
} from '../../services/classroom/teacherService';

const MAX_REPLY_DEPTH = 2;
const QUICK_DISCUSSION_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

const mapDiscussionPostsWithReaction = (
  posts: TeacherDiscussionPost[],
  postId: string,
  reactions: DiscussionReactionSummary[]
): TeacherDiscussionPost[] =>
  posts.map((post) => {
    if (post.id === postId) return { ...post, reactions };
    if (post.replies.length === 0) return post;
    return { ...post, replies: mapDiscussionPostsWithReaction(post.replies, postId, reactions) };
  });

const sortDiscussionReactions = (reactions: DiscussionReactionSummary[]): DiscussionReactionSummary[] =>
  [...reactions].sort((left, right) => {
    if (left.reactedByMe !== right.reactedByMe) return left.reactedByMe ? -1 : 1;
    const leftOrder = QUICK_DISCUSSION_REACTIONS.indexOf(left.emoji as (typeof QUICK_DISCUSSION_REACTIONS)[number]);
    const rightOrder = QUICK_DISCUSSION_REACTIONS.indexOf(right.emoji as (typeof QUICK_DISCUSSION_REACTIONS)[number]);
    const leftRank = leftOrder >= 0 ? leftOrder : Number.MAX_SAFE_INTEGER;
    const rightRank = rightOrder >= 0 ? rightOrder : Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    if (left.count !== right.count) return right.count - left.count;
    return left.emoji.localeCompare(right.emoji);
  });

const toggleReactionOptimistically = (
  reactions: DiscussionReactionSummary[] | null | undefined,
  emoji: string,
  viewerUserId: string
): DiscussionReactionSummary[] => {
  const current = [...(reactions ?? [])];
  const existing = current.find((reaction) => reaction.reactedByMe);

  const removeEmoji = (targetEmoji: string) => {
    const next: DiscussionReactionSummary[] = [];
    current.forEach((reaction) => {
      if (reaction.emoji !== targetEmoji) {
        next.push({ ...reaction });
        return;
      }
      const count = reaction.count - 1;
      if (count <= 0) return;
      next.push({ ...reaction, count, reactedByMe: false });
    });
    return next;
  };

  if (existing?.emoji === emoji) {
    return sortDiscussionReactions(removeEmoji(emoji));
  }

  let next = existing ? removeEmoji(existing.emoji) : current.map((reaction) => ({ ...reaction }));
  const match = next.find((reaction) => reaction.emoji === emoji);
  if (match) {
    match.count += 1;
    match.reactedByMe = true;
  } else {
    next.push({ emoji, count: 1, reactedByMe: true });
  }
  return sortDiscussionReactions(next);
};

/** Matches classroom teacher cards (e.g. TableCard, InfoPanel). */
const cardShadow = '0 2px 12px rgba(0, 0, 0, 0.08)';
const cardShadowHover = '0 4px 16px rgba(0, 0, 0, 0.1)';

const BoardRoot = styled.div`
  display: grid;
  gap: 1rem;
  min-width: 0;
  width: 100%;
`;

const BoardHead = styled.header`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem 1.5rem;
  border-radius: 16px;
  background: var(--surface);
  padding: 1.25rem 1.35rem 1.3rem;
  box-shadow: ${cardShadow};
  border: 1px solid var(--outline);
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--primaryDark),
      var(--primary) 55%,
      color-mix(in srgb, var(--primary) 65%, var(--surface)) 100%
    );
  }
`;

const HeadText = styled.div`
  position: relative;
  min-width: 0;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.4rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary);
`;

const HeadTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.15rem, 2vw, 1.35rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text);
  line-height: 1.2;
`;

const HeadSubtitle = styled.p`
  margin: 0.5rem 0 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 36rem;
`;

const HeadActions = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem 0.75rem;
  @media (max-width: 640px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const BoardStatus = styled.span<{ $locked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.38rem 0.88rem;
  white-space: nowrap;
  border: 1px solid
    ${({ $locked }) =>
      $locked
        ? 'color-mix(in srgb, var(--warning, #e8a23c) 40%, var(--outline))'
        : 'color-mix(in srgb, var(--success, #22a06b) 40%, var(--outline))'};
  background: ${({ $locked }) =>
    $locked
      ? 'color-mix(in srgb, var(--warning, #e8a23c) 20%, transparent)'
      : 'color-mix(in srgb, var(--success, #22a06b) 18%, transparent)'};
  color: ${({ $locked }) => ($locked ? 'var(--warning, #b45309)' : 'var(--success, #15803d)')};
`;

const StatusDot = styled.span<{ $locked: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $locked }) => ($locked ? 'var(--warning, #e8a23c)' : 'var(--success, #22a06b)')};
  box-shadow: ${({ $locked }) =>
    $locked
      ? '0 0 0 2px color-mix(in srgb, var(--warning, #e8a23c) 35%, transparent)'
      : '0 0 0 2px color-mix(in srgb, var(--success, #22a06b) 35%, transparent)'};
`;

const LockButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  padding: 0.52rem 1rem;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

const ComposerCard = styled.form`
  display: grid;
  gap: 0.9rem;
  border-radius: 16px;
  background: var(--surface);
  padding: 1.2rem 1.35rem;
  box-shadow: ${cardShadow};
  border: 1px solid var(--outline);
`;

const ComposerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ComposerTitle = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const ComposerHint = styled.span`
  font-size: 0.8125rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
`;

const ComposerFields = styled.div`
  display: grid;
  gap: 0.7rem;
  @media (min-width: 560px) {
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 0.8rem;
  }
`;

const ComposerTextarea = styled.textarea`
  width: 100%;
  margin: 0;
  min-height: 5rem;
  resize: vertical;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surfaceVariant);
  color: var(--text);
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.7rem 0.85rem;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  &::placeholder {
    color: color-mix(in srgb, var(--textSecondary, #64748b) 65%, var(--surface));
  }
  &:hover:not(:disabled) {
    border-color: color-mix(in srgb, var(--text) 18%, var(--outline));
  }
  &:focus {
    outline: none;
    background: var(--surface);
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 22%, transparent);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  justify-self: end;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: var(--onPrimary);
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  padding: 0.58rem 1.1rem;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  @media (min-width: 560px) {
    height: fit-content;
    padding: 0.62rem 1.15rem;
  }
  &:hover:not(:disabled) {
    background: var(--primaryDark);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ThreadsList = styled.section`
  display: grid;
  gap: 0.875rem;
`;

const PostCard = styled.article<{ $pinned?: boolean }>`
  position: relative;
  border-radius: 14px;
  background: var(--surface);
  padding: 1.05rem 1.15rem 1.1rem;
  display: grid;
  gap: 0.75rem;
  border: 1px solid var(--outline);
  box-shadow: ${cardShadow};
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  ${({ $pinned }) =>
    $pinned
      ? `
    border-color: color-mix(in srgb, var(--primary) 28%, var(--outline));
    background: color-mix(in srgb, var(--primary) 7%, var(--surface));
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 3px;
      border-radius: 14px 0 0 14px;
      background: linear-gradient(180deg, var(--primaryDark), var(--primary));
    }
  `
      : ''}
  &:hover {
    box-shadow: ${cardShadowHover};
    border-color: color-mix(in srgb, var(--primary) 22%, var(--outline));
  }
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--onPrimary);
  background: linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 100%);
  flex-shrink: 0;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.6rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const AuthorName = styled.strong`
  font-weight: 600;
  color: var(--text);
`;

const TimeStamp = styled.span`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.78rem;
  opacity: 0.92;
`;

const EditedLabel = styled.span`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.72rem;
  font-style: italic;
  opacity: 0.92;
`;

const MetaTag = styled.span<{ $variant: 'pinned' | 'removed' }>`
  font-style: normal;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.68rem;
  padding: 0.2rem 0.55rem;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'pinned'
        ? 'color-mix(in srgb, var(--primary) 35%, var(--outline))'
        : 'color-mix(in srgb, var(--warning, #e8a23c) 40%, var(--outline))'};
  background: ${({ $variant }) =>
    $variant === 'pinned'
      ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))'
      : 'color-mix(in srgb, var(--warning, #e8a23c) 18%, transparent)'};
  color: ${({ $variant }) =>
    $variant === 'pinned' ? 'var(--primary)' : 'var(--warning, #b45309)'};
`;

const Body = styled.p`
  margin: 0;
  color: var(--text);
  font-size: 0.875rem;
  line-height: 1.55;
  white-space: pre-wrap;
`;

const PostActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.1rem;
`;

const ActionButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 999px;
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const RepliesRail = styled.div`
  position: relative;
  margin-left: 0.25rem;
  padding-left: 1rem;
  display: grid;
  gap: 0.55rem;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.12rem;
    bottom: 0.12rem;
    width: 3px;
    border-radius: 3px;
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--primary) 50%, transparent),
      color-mix(in srgb, var(--outline) 85%, transparent)
    );
  }
`;

const ReplyCard = styled.article`
  border-radius: 12px;
  background: var(--surfaceVariant);
  padding: 0.7rem 0.8rem;
  display: grid;
  gap: 0.45rem;
  border: 1px solid color-mix(in srgb, var(--outline) 90%, transparent);
`;

const ReplyForm = styled.form`
  position: relative;
  margin-left: 0.25rem;
  padding-left: 1rem;
  display: grid;
  gap: 0.45rem;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 3px;
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--textSecondary, #64748b) 45%, transparent),
      color-mix(in srgb, var(--outline) 75%, transparent)
    );
  }
`;

const EditForm = styled(ReplyForm)``;

const EditActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ReplyLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ReplyFields = styled.div`
  display: grid;
  gap: 0.65rem;
  @media (min-width: 560px) {
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 0.7rem;
  }
`;

const ReplyInput = styled(ComposerTextarea)`
  min-height: 4rem;
  font-size: 0.84rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.4rem;
  border-radius: 16px;
  background: var(--surfaceVariant);
  border: 1px dashed var(--outline);
  padding: 2rem 1.35rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const EmptyIcon = styled.span`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--primary) 14%, var(--surfaceVariant));
  margin-bottom: 0.25rem;
  position: relative;
  border: 1px solid color-mix(in srgb, var(--primary) 22%, var(--outline));
  &::before,
  &::after {
    content: '';
    position: absolute;
    background: var(--primary);
    border-radius: 2px;
    opacity: 0.5;
  }
  &::before {
    width: 22px;
    height: 3px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -8px);
  }
  &::after {
    width: 3px;
    height: 14px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -2px);
  }
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  max-width: 24rem;
  line-height: 1.45;
`;

const MessageCard = styled.div`
  border-radius: 16px;
  padding: 1.25rem 1.2rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  border: 1px solid var(--outline);
  background: var(--surface);
  box-shadow: ${cardShadow};
`;

type Props = {
  classId: string;
  currentUserId?: string | null;
};

function formatWhen(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'just now';
  const diffMin = Math.floor((Date.now() - ts) / 60000);
  if (diffMin <= 0) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 14) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

function isEdited(post: TeacherDiscussionPost): boolean {
  const created = new Date(post.created_at).getTime();
  const updated = new Date(post.updated_at).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) return false;
  return updated - created > 1000;
}

function renderPostBody(post: TeacherDiscussionPost): string {
  if (post.status === 'removed') {
    return 'This post was removed by a teacher.';
  }
  return post.body;
}

function AuthorAvatar({ name }: { name: string }) {
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return <Avatar aria-hidden>{initial}</Avatar>;
}

type PostActionMutation = UseMutationResult<
  unknown,
  Error,
  { postId: string; updates: Parameters<typeof teacherService.updateDiscussionPost>[1] },
  unknown
>;

type DiscussionPostItemProps = {
  depth: number;
  post: TeacherDiscussionPost;
  canPost: boolean;
  currentUserId?: string | null;
  activeEditPostId: string | null;
  replyDraftByParent: Record<string, string>;
  editDraftByPost: Record<string, string>;
  threadExpandedById: Record<string, boolean>;
  postActionMutation: PostActionMutation;
  createReplyPending: boolean;
  reactionPending: boolean;
  onReplyDraftChange: (postId: string, value: string) => void;
  onEditDraftChange: (postId: string, value: string) => void;
  onSetActiveEditPostId: (postId: string | null) => void;
  onSubmitReply: (event: React.FormEvent<HTMLFormElement>, parentPostId: string) => void;
  onSubmitEdit: (event: React.FormEvent<HTMLFormElement>, postId: string) => void;
  onToggleThread: (postId: string, nextExpanded: boolean) => void;
  onToggleReaction: (postId: string, emoji: string) => void;
};

function DiscussionPostItem({
  depth,
  post,
  canPost,
  currentUserId,
  activeEditPostId,
  replyDraftByParent,
  editDraftByPost,
  threadExpandedById,
  postActionMutation,
  createReplyPending,
  reactionPending,
  onReplyDraftChange,
  onEditDraftChange,
  onSetActiveEditPostId,
  onSubmitReply,
  onSubmitEdit,
  onToggleThread,
  onToggleReaction,
}: DiscussionPostItemProps) {
  const canReply = depth < MAX_REPLY_DEPTH && canPost;
  const canEdit =
    Boolean(currentUserId) && post.author_id === currentUserId && post.status === 'published';
  const hasReplies = post.replies.length > 0;
  const isExpanded = threadExpandedById[post.id] ?? depth === 0;
  const isEditing = activeEditPostId === post.id;
  const Card = depth === 0 ? PostCard : ReplyCard;

  return (
    <Card $pinned={depth === 0 ? post.is_pinned : undefined}>
      <Meta>
        <AuthorAvatar name={post.author_name} />
        <AuthorName>{post.author_name}</AuthorName>
        <TimeStamp>{formatWhen(post.created_at)}</TimeStamp>
        {isEdited(post) ? <EditedLabel>edited</EditedLabel> : null}
        {depth === 0 && post.is_pinned ? <MetaTag $variant="pinned">Pinned</MetaTag> : null}
        {post.status === 'removed' ? <MetaTag $variant="removed">Removed</MetaTag> : null}
      </Meta>
      {isEditing ? (
        <EditForm onSubmit={(event) => onSubmitEdit(event, post.id)}>
          <ReplyInput
            value={editDraftByPost[post.id] ?? ''}
            onChange={(event) => onEditDraftChange(post.id, event.target.value)}
            disabled={postActionMutation.isPending}
            rows={depth === 0 ? 3 : 2}
            aria-label="Edit post"
          />
          <EditActions>
            <ActionButton
              type="button"
              onClick={() => onSetActiveEditPostId(null)}
              disabled={postActionMutation.isPending}
            >
              Cancel
            </ActionButton>
            <PrimaryButton
              type="submit"
              disabled={postActionMutation.isPending || !(editDraftByPost[post.id] ?? '').trim()}
            >
              Save changes
            </PrimaryButton>
          </EditActions>
        </EditForm>
      ) : (
        <Body>
          {post.status === 'removed' ? renderPostBody(post) : <LinkedText text={post.body} />}
        </Body>
      )}
      {!isEditing && post.status === 'published' ? (
        <DiscussionReactionBar
          reactions={post.reactions}
          disabled={reactionPending}
          onToggleReaction={(emoji) => onToggleReaction(post.id, emoji)}
        />
      ) : null}
      <PostActions>
        {canEdit && !isEditing ? (
          <ActionButton
            type="button"
            onClick={() => {
              onSetActiveEditPostId(post.id);
              onEditDraftChange(post.id, post.body);
            }}
            disabled={postActionMutation.isPending}
          >
            Edit
          </ActionButton>
        ) : null}
        {depth === 0 ? (
          <ActionButton
            type="button"
            onClick={() =>
              postActionMutation.mutate({
                postId: post.id,
                updates: { is_pinned: !post.is_pinned },
              })
            }
            disabled={postActionMutation.isPending}
          >
            {post.is_pinned ? 'Unpin' : 'Pin'}
          </ActionButton>
        ) : null}
        <ActionButton
          type="button"
          onClick={() =>
            postActionMutation.mutate({
              postId: post.id,
              updates:
                post.status === 'removed'
                  ? { status: 'published', hidden_at: null, hidden_by: null }
                  : {
                      status: 'removed',
                      hidden_at: new Date().toISOString(),
                      hidden_by: currentUserId ?? null,
                    },
            })
          }
          disabled={postActionMutation.isPending}
        >
          {post.status === 'removed' ? 'Restore' : 'Remove'}
        </ActionButton>
        {hasReplies ? (
          <ActionButton type="button" onClick={() => onToggleThread(post.id, !isExpanded)}>
            {isExpanded
              ? 'Hide replies'
              : `View ${post.replies.length} repl${post.replies.length === 1 ? 'y' : 'ies'}`}
          </ActionButton>
        ) : null}
      </PostActions>

      {hasReplies && isExpanded ? (
        <RepliesRail>
          {post.replies.map((reply) => (
            <DiscussionPostItem
              key={reply.id}
              depth={depth + 1}
              post={reply}
              canPost={canPost}
              currentUserId={currentUserId}
              activeEditPostId={activeEditPostId}
              replyDraftByParent={replyDraftByParent}
              editDraftByPost={editDraftByPost}
              threadExpandedById={threadExpandedById}
              postActionMutation={postActionMutation}
              createReplyPending={createReplyPending}
              reactionPending={reactionPending}
              onReplyDraftChange={onReplyDraftChange}
              onEditDraftChange={onEditDraftChange}
              onSetActiveEditPostId={onSetActiveEditPostId}
              onSubmitReply={onSubmitReply}
              onSubmitEdit={onSubmitEdit}
              onToggleThread={onToggleThread}
              onToggleReaction={onToggleReaction}
            />
          ))}
        </RepliesRail>
      ) : null}

      {canReply && !isEditing ? (
        <ReplyForm onSubmit={(event) => onSubmitReply(event, post.id)}>
          <ReplyLabel>Reply to {post.author_name}</ReplyLabel>
          <ReplyFields>
            <ReplyInput
              value={replyDraftByParent[post.id] ?? ''}
              onChange={(event) => onReplyDraftChange(post.id, event.target.value)}
              placeholder={canPost ? 'Reply as teacher…' : 'Board is locked.'}
              disabled={!canPost || createReplyPending}
              rows={2}
            />
            <PrimaryButton
              type="submit"
              disabled={!canPost || createReplyPending || !(replyDraftByParent[post.id] ?? '').trim()}
            >
              Send reply
            </PrimaryButton>
          </ReplyFields>
        </ReplyForm>
      ) : null}
    </Card>
  );
}

export default function ClassDiscussionBoard({ classId, currentUserId }: Props) {
  const queryClient = useQueryClient();
  const [newPostBody, setNewPostBody] = useState('');
  const [replyDraftByParent, setReplyDraftByParent] = useState<Record<string, string>>({});
  const [editDraftByPost, setEditDraftByPost] = useState<Record<string, string>>({});
  const [activeEditPostId, setActiveEditPostId] = useState<string | null>(null);
  const [threadExpandedById, setThreadExpandedById] = useState<Record<string, boolean>>({});
  const invalidateScheduledRef = useRef(false);
  const discussionQueryKey = useMemo(() => ['classroom', 'teacher', 'discussion', classId] as const, [classId]);
  const discussionQuery = useQuery({
    queryKey: discussionQueryKey,
    queryFn: () => teacherService.getClassDiscussion(classId),
    enabled: Boolean(classId),
  });

  const posts = discussionQuery.data?.posts ?? [];
  const settings = discussionQuery.data?.settings;
  const isLocked = Boolean(settings?.is_locked);

  const invalidateDiscussion = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: discussionQueryKey,
      }),
    [queryClient, discussionQueryKey]
  );

  const scheduleDiscussionInvalidation = useCallback(() => {
    if (invalidateScheduledRef.current) return;
    invalidateScheduledRef.current = true;
    queueMicrotask(() => {
      invalidateScheduledRef.current = false;
      void invalidateDiscussion();
    });
  }, [invalidateDiscussion]);

  useEffect(() => {
    if (!classId) return undefined;
    return teacherService.subscribeToClassDiscussion(classId, () => {
      scheduleDiscussionInvalidation();
    });
  }, [classId, scheduleDiscussionInvalidation]);

  const createPostMutation = useMutation({
    mutationFn: (body: string) =>
      teacherService.createDiscussionPost({
        classId,
        body,
        authorId: currentUserId ?? null,
      }),
    onSuccess: () => {
      setNewPostBody('');
      void invalidateDiscussion();
    },
  });

  const createReplyMutation = useMutation({
    mutationFn: (payload: { parentPostId: string; body: string }) =>
      teacherService.createDiscussionReply({
        classId,
        parentPostId: payload.parentPostId,
        body: payload.body,
        authorId: currentUserId ?? null,
      }),
    onSuccess: (_result, variables) => {
      setReplyDraftByParent((prev) => ({ ...prev, [variables.parentPostId]: '' }));
      setThreadExpandedById((prev) => ({ ...prev, [variables.parentPostId]: true }));
      void invalidateDiscussion();
    },
  });

  const postActionMutation = useMutation({
    mutationFn: (payload: { postId: string; updates: Parameters<typeof teacherService.updateDiscussionPost>[1] }) =>
      teacherService.updateDiscussionPost(payload.postId, payload.updates),
    onSuccess: () => {
      void invalidateDiscussion();
    },
  });

  const lockMutation = useMutation({
    mutationFn: (locked: boolean) =>
      teacherService.updateDiscussionSettings(classId, locked, currentUserId ?? null),
    onSuccess: () => {
      void invalidateDiscussion();
    },
  });

  const toggleReactionMutation = useMutation({
    mutationFn: (payload: { postId: string; emoji: string }) =>
      teacherService.toggleDiscussionPostReaction(payload.postId, payload.emoji),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: discussionQueryKey });
      const previous = queryClient.getQueryData<TeacherClassDiscussion>(discussionQueryKey);
      if (!previous || !currentUserId) return { previous };

      const findPost = (posts: TeacherDiscussionPost[]): TeacherDiscussionPost | null => {
        for (const post of posts) {
          if (post.id === payload.postId) return post;
          const nested = findPost(post.replies);
          if (nested) return nested;
        }
        return null;
      };
      const target = findPost(previous.posts);
      if (!target) return { previous };

      const nextReactions = toggleReactionOptimistically(target.reactions, payload.emoji, currentUserId);
      queryClient.setQueryData<TeacherClassDiscussion>(discussionQueryKey, {
        ...previous,
        posts: mapDiscussionPostsWithReaction(previous.posts, payload.postId, nextReactions),
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(discussionQueryKey, context.previous);
      }
    },
    onSuccess: (result, variables) => {
      if (!result.ok) return;
      queryClient.setQueryData<TeacherClassDiscussion>(discussionQueryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          posts: mapDiscussionPostsWithReaction(current.posts, variables.postId, result.data.reactions),
        };
      });
    },
  });

  const canPost = useMemo(() => !settings?.is_locked, [settings?.is_locked]);

  const submitPost = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = newPostBody.trim();
    if (!body || !canPost || createPostMutation.isPending) return;
    createPostMutation.mutate(body);
  };

  const submitReply = (event: React.FormEvent<HTMLFormElement>, parentPostId: string) => {
    event.preventDefault();
    const draft = (replyDraftByParent[parentPostId] ?? '').trim();
    if (!draft || !canPost || createReplyMutation.isPending) return;
    createReplyMutation.mutate({ parentPostId, body: draft });
  };

  const changeReplyDraft = (postId: string, value: string) => {
    setReplyDraftByParent((prev) => ({ ...prev, [postId]: value }));
  };

  const changeEditDraft = (postId: string, value: string) => {
    setEditDraftByPost((prev) => ({ ...prev, [postId]: value }));
  };

  const submitEdit = (event: React.FormEvent<HTMLFormElement>, postId: string) => {
    event.preventDefault();
    const draft = (editDraftByPost[postId] ?? '').trim();
    if (!draft || postActionMutation.isPending) return;
    postActionMutation.mutate(
      { postId, updates: { body: draft } },
      {
        onSuccess: () => {
          setEditDraftByPost((prev) => ({ ...prev, [postId]: '' }));
          setActiveEditPostId((current) => (current === postId ? null : current));
        },
      }
    );
  };

  const toggleThread = (postId: string, nextExpanded: boolean) => {
    setThreadExpandedById((prev) => ({ ...prev, [postId]: nextExpanded }));
  };

  const onToggleReaction = (postId: string, emoji: string) => {
    if (toggleReactionMutation.isPending) return;
    toggleReactionMutation.mutate({ postId, emoji });
  };

  if (discussionQuery.isLoading) {
    return (
      <BoardRoot>
        <MessageCard role="status">Loading discussion board…</MessageCard>
      </BoardRoot>
    );
  }

  if (discussionQuery.isError) {
    return (
      <BoardRoot>
        <MessageCard role="alert">Unable to load discussion board.</MessageCard>
      </BoardRoot>
    );
  }

  return (
    <BoardRoot>
      <BoardHead>
        <HeadText>
          <Eyebrow>Moderate</Eyebrow>
          <HeadTitle>Discussion board</HeadTitle>
          <HeadSubtitle>
            Students post and reply here. Pin or remove content, and lock the board when you need to pause the
            conversation.
          </HeadSubtitle>
        </HeadText>
        <HeadActions>
          <BoardStatus $locked={isLocked}>
            <StatusDot $locked={isLocked} aria-hidden />
            {isLocked ? 'Board locked' : 'Open for posting'}
          </BoardStatus>
          <LockButton type="button" onClick={() => lockMutation.mutate(!isLocked)} disabled={lockMutation.isPending}>
            {isLocked ? 'Unlock board' : 'Lock board'}
          </LockButton>
        </HeadActions>
      </BoardHead>

      <ComposerCard onSubmit={submitPost}>
        <ComposerHeader>
          <ComposerTitle>Start a topic</ComposerTitle>
          <ComposerHint>
            {canPost
              ? 'Your message is visible to everyone in this class.'
              : 'Posting is paused while the board is locked. Unlock to add a post.'}
          </ComposerHint>
        </ComposerHeader>
        <ComposerFields>
          <ComposerTextarea
            value={newPostBody}
            onChange={(event) => setNewPostBody(event.target.value)}
            placeholder={
              canPost ? 'Post an update or prompt for your students…' : 'Board is locked. Unlock to post.'
            }
            disabled={!canPost || createPostMutation.isPending}
            rows={3}
          />
          <PrimaryButton type="submit" disabled={!canPost || createPostMutation.isPending || !newPostBody.trim()}>
            Post to class
          </PrimaryButton>
        </ComposerFields>
      </ComposerCard>

      {posts.length === 0 ? (
        <EmptyState role="status">
          <EmptyIcon aria-hidden />
          <EmptyTitle>No threads yet</EmptyTitle>
          <EmptyText>When students post, you will see threads here. You can also start the conversation.</EmptyText>
        </EmptyState>
      ) : (
        <ThreadsList aria-label="Discussion threads">
          {posts.map((post) => (
            <DiscussionPostItem
              key={post.id}
              depth={0}
              post={post}
              canPost={canPost}
              currentUserId={currentUserId}
              activeEditPostId={activeEditPostId}
              replyDraftByParent={replyDraftByParent}
              editDraftByPost={editDraftByPost}
              threadExpandedById={threadExpandedById}
              postActionMutation={postActionMutation}
              createReplyPending={createReplyMutation.isPending}
              reactionPending={toggleReactionMutation.isPending}
              onReplyDraftChange={changeReplyDraft}
              onEditDraftChange={changeEditDraft}
              onSetActiveEditPostId={setActiveEditPostId}
              onSubmitReply={submitReply}
              onSubmitEdit={submitEdit}
              onToggleThread={toggleThread}
              onToggleReaction={onToggleReaction}
            />
          ))}
        </ThreadsList>
      )}
    </BoardRoot>
  );
}
